import { useEffect, useState } from 'react';
import NotificationsServiceDB from '../services/notificationsServiceDB';

// cacheInvalidation's INVITES_CHANGED_EVENT, spelled out as useTripState does:
// that module imports half the app's caches, and every page's nav reads this
// hook.
const INVITES_CHANGED_EVENT = 'invitesChanged';

// Several screens hold the count at once (the desktop bell, the tab bar, the
// profile bell, /you) and notifySubscribers calls them all in one loop, so
// concurrent loads share one request — one fetch per update, one value.
let inflight = null;
function fetchUnreadCount() {
  if (!inflight) {
    inflight = NotificationsServiceDB.getUnreadCount().finally(() => { inflight = null; });
  }
  return inflight;
}

/**
 * The signed-in user's unread notification count, kept live through
 * NotificationsServiceDB's subscription (mark-as-read / remove notify
 * subscribers, so callers never refresh by hand). 0 when signed out.
 *
 * Shared by the desktop nav's bell, the phone tab bar's You badge, the
 * profile owner bar and /you; concurrent loads share one request, so they
 * can't disagree. It also takes every count a response carries
 * (onUnreadCount), so the badge reads the number the open bell just read —
 * after a tag is retired or a notice archived without a subscriber refresh —
 * and re-reads after an invitation is answered anywhere, which retires its
 * notice (C292).
 */
export default function useUnreadNotificationCount(currentUser) {
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!currentUser) {
        setUnreadCount(0);
        return;
      }
      try {
        const count = await fetchUnreadCount();
        if (!cancelled) setUnreadCount(count);
      } catch (error) {
        console.error('Error loading unread count:', error);
      }
    };

    load();
    if (!currentUser) return () => { cancelled = true; };

    // Subscribed, but with nothing to fetch: every update the service sends
    // follows a list fetch whose count already reached onUnreadCount below,
    // so asking /notifications/count again was one extra request per update
    // (PH-12-03). The subscription itself stays — the first subscriber starts
    // the poll and the live stream, and with the bell closed this hook is it.
    const unsubscribe = NotificationsServiceDB.subscribe(() => {});
    const offCount = NotificationsServiceDB.onUnreadCount((count) => {
      if (!cancelled) setUnreadCount(count);
    });
    window.addEventListener(INVITES_CHANGED_EVENT, load);
    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
      offCount();
      window.removeEventListener(INVITES_CHANGED_EVENT, load);
    };
  }, [currentUser]);

  return unreadCount;
}
